import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import ManagerHeader from '../components/ManagerHeader';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerMobileSidebar from '../components/ManagerMobileSidebar';
import ManagerCreditModal from '../components/ManagerCreditModal';
import DarkSelect from '../components/DarkSelect';
import Footer from '../components/Footer';
import api from '../services/api';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface Payout {
  id: number;
  merchant_id: number;
  merchant_reference_number: string;
  viyona_transaction_id?: string;
  account_name: string;
  account_number: string;
  amount: number;
  net_amount?: number;
  transaction_fees_percentage?: number;
  transaction_fee_amount?: number;
  ifsc_code: string;
  bank_name: string;
  bank_branch?: string;
  transfer_type: 'IMPS' | 'NEFT' | 'RTGS';
  customer_email?: string;
  customer_phone?: string;
  remark?: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  error_message?: string;
  bank_reference_number?: string;
  transaction_date?: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  merchant_wallet_balance?: number;
}

interface Merchant {
  id: number;
  ownerName: string;
  email: string;
  created_at: string;
  businessName?: string;
  businessType?: string;
  phone?: string;
  city?: string;
  state?: string;
  wallet?: number;
  status?: boolean;
}

interface TransactionFilters {
  status: string;
  fromDate: string;
  toDate: string;
  accountNumber: string;
  transactionId: string;
  merchantReference: string;
  bankReference: string;
  amount: string;
  selectedMerchant: string;
}

interface TransactionStats {
  totalTransactions: number;
  totalCredits: number;
  totalDebits: number;
  creditCount: number;
  debitCount: number;
  totalPayouts: number;
  completed: number;
  pending: number;
}

const ManagerCreditHistory: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor, tableColor } = useManagerTheme();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [stats, setStats] = useState<TransactionStats>({
    totalTransactions: 0,
    totalCredits: 0,
    totalDebits: 0,
    creditCount: 0,
    debitCount: 0,
    totalPayouts: 0,
    completed: 0,
    pending: 0
  });
  const loadingStartRef = useRef(0);
  const [filters, setFilters] = useState<TransactionFilters>({
    status: '',
    fromDate: '',
    toDate: '',
    accountNumber: '',
    transactionId: '',
    merchantReference: '',
    bankReference: '',
    amount: '',
    selectedMerchant: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage] = useState(50);

  useEffect(() => {
    fetchMerchants();
    fetchPayouts();
  }, [currentPage]);

  useEffect(() => {
    fetchSummaryStats();
  }, []);

  const fetchMerchants = async () => {
    try {
      const response = await api.get('/merchants');
      console.log('Merchants API Response:', response.data);
      if (response.data.success) {
        setMerchants(response.data.data || []);
        console.log('Merchants set:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    }
  };

  const fetchSummaryStats = async () => {
    try {
      // Always fetch global totals without any filters
      const queryParams = new URLSearchParams();
      queryParams.append('limit', '10000'); // Get all payouts for global stats
      
      const response = await api.get(`/dashboard/manager/payouts?${queryParams.toString()}`);
      
      if (response.data.success && response.data.data.payouts) {
        const allPayouts = response.data.data.payouts;
        
        const globalStats = {
          totalTransactions: allPayouts.length,
          totalCredits: 0, // Payouts are debits, not credits
          totalDebits: allPayouts.reduce((sum: number, payout: Payout) => sum + Number(payout.amount || 0), 0),
          creditCount: 0,
          debitCount: allPayouts.length,
          totalPayouts: allPayouts.length,
          completed: allPayouts.filter((p: Payout) => p.status === 'SUCCESS').length,
          pending: allPayouts.filter((p: Payout) => p.status === 'PENDING' || p.status === 'PROCESSING').length
        };
        
        console.log('Global summary stats calculated:', globalStats);
        setStats(globalStats);
      } else {
        console.error('Failed to fetch summary stats:', response.data);
      }
    } catch (error) {
      console.error('Error fetching summary stats:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', recordsPerPage.toString());
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.fromDate) queryParams.append('date_from', filters.fromDate);
      if (filters.toDate) queryParams.append('date_to', filters.toDate);
      if (filters.accountNumber) queryParams.append('account_number', filters.accountNumber);
      if (filters.transactionId) queryParams.append('transaction_id', filters.transactionId);
      if (filters.merchantReference) queryParams.append('merchant_reference', filters.merchantReference);
      if (filters.bankReference) queryParams.append('bank_reference', filters.bankReference);
      if (filters.amount) queryParams.append('amount', filters.amount);
      if (filters.selectedMerchant) queryParams.append('merchant_id', filters.selectedMerchant);

      const response = await api.get(`/dashboard/manager/payouts?${queryParams.toString()}`);
      
      if (response.data.success) {
        console.log('🔍 API Response:', response.data);
        console.log('🔍 Payouts data:', response.data.data.payouts);
        
        // Debug first payout's wallet balance
        if (response.data.data.payouts && response.data.data.payouts.length > 0) {
          const firstPayout = response.data.data.payouts[0];
          console.log('🔍 First payout wallet balance:', firstPayout.merchant_wallet_balance);
        }
        
        setPayouts(response.data.data.payouts || []);
        
        // Update pagination info
        if (response.data.data.pagination) {
          setTotalPages(response.data.data.pagination.totalPages || 1);
          setTotalRecords(response.data.data.pagination.totalRecords || 0);
        }
      } else {
        setError('Failed to fetch payouts');
      }
    } catch (err: any) {
      console.error('Error fetching payouts:', err);
      setError('Failed to fetch payouts');
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const handleFilterChange = (key: keyof TransactionFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      fromDate: '',
      toDate: '',
      accountNumber: '',
      transactionId: '',
      merchantReference: '',
      bankReference: '',
      amount: '',
      selectedMerchant: ''
    });
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchPayouts();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionTypeBadge = (type: string) => {
    const isCredit = type.toLowerCase().includes('credit');
    const isDebit = type.toLowerCase().includes('debit');
    
    let config;
    if (isCredit) {
      config = { color: '#10b981', bg: '#d1fae5', text: 'Credit', icon: '↗️' };
    } else if (isDebit) {
      config = { color: '#ef4444', bg: '#fee2e2', text: 'Debit', icon: '↙️' };
    } else {
      config = { color: '#6b7280', bg: '#f3f4f6', text: type, icon: '🔄' };
    }

    return (
      <span 
        className="transaction-type-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <span>{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      'admin': { color: '#dc2626', bg: '#fee2e2', text: 'Admin' },
      'manager': { color: '#2563eb', bg: '#dbeafe', text: 'Manager' },
      'merchant': { color: '#059669', bg: '#d1fae5', text: 'Merchant' },
      'user': { color: '#6b7280', bg: '#f3f4f6', text: 'User' }
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.user;

    return (
      <span 
        className="role-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '600',
          textTransform: 'uppercase'
        }}
      >
        {config.text}
      </span>
    );
  };

  const handleCreditMoney = () => {
    setShowCreditModal(true);
  };

  const handleMyProfile = () => {
    window.location.href = '/manager/account';
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
        <ManagerHeader
          user={user}
          onRefresh={() => {}}
          onCreditMoney={handleCreditMoney}
          onMyProfile={handleMyProfile}
          onLogout={handleLogout}
        />
        <div className="flex flex-1">
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="credit" /></div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-8 backdrop-blur-sm text-slate-200" style={{ backgroundColor: contentColor }}>
            <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
            <p className="text-slate-200 text-base m-0">Loading payout history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      <ManagerMobileSidebar
        showMobileSidebar={showMobileSidebar}
        setShowMobileSidebar={setShowMobileSidebar}
        activeTab="credit"
        onCreditMoney={handleCreditMoney}
        onLogout={handleLogout}
      />
      <ManagerHeader
        user={user}
        onRefresh={fetchPayouts}
        onCreditMoney={handleCreditMoney}
        onMyProfile={handleMyProfile}
        onLogout={handleLogout}
      />
      <div className="flex flex-1">
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="credit" /></div>
        <div className="flex-1 flex flex-col text-slate-200 p-6 md:p-8" style={{ backgroundColor: contentColor }}>
          <div className="mb-4">
            <h1 className="text-slate-100 text-2xl font-bold m-0">Merchant Report History</h1>
            <p className="text-slate-300 text-base font-medium mt-1 m-0">Monitor all credit and debit transactions across the platform</p>
          </div>
          <div className="mb-6 flex gap-5 flex-wrap justify-start items-center">
            <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-red-900 to-red-950 border-2 border-red-500 shadow-lg">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-red-600 to-red-800 border-2 border-white/20 text-white">💸</div>
              <div className="flex-1">
                <h3 className="text-base font-black text-slate-300 uppercase tracking-wider m-0">
                  Total Debits Amount
                  <span className="ml-1 text-sm font-black text-red-200 normal-case">- {formatCurrency(stats.totalDebits)}</span>
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-600 shadow-lg">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-emerald-600 to-emerald-800 border-2 border-white/20 text-white">📊</div>
              <div className="flex-1">
                <h3 className="text-base font-black text-slate-300 uppercase tracking-wider m-0">
                  Total Payouts
                  <span className="ml-1 text-sm font-black text-slate-200 normal-case">- {stats.totalPayouts}</span>
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-600 shadow-lg">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-blue-600 to-blue-800 border-2 border-white/20 text-white">✅</div>
              <div className="flex-1">
                <h3 className="text-base font-black text-slate-300 uppercase tracking-wider m-0">
                  Completed
                  <span className="ml-1 text-sm font-black text-slate-200 normal-case">- {stats.completed}</span>
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-600 shadow-lg">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-amber-600 to-amber-800 border-2 border-white/20 text-white">⏳</div>
              <div className="flex-1">
                <h3 className="text-base font-black text-slate-300 uppercase tracking-wider m-0">
                  Pending
                  <span className="ml-1 text-sm font-black text-slate-200 normal-case">- {stats.pending}</span>
                </h3>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-6 mb-6 border border-white/[0.06] shadow-xl shadow-black/25" style={{ backgroundColor: sectionColor }}>
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-white text-xl font-bold m-0">
                Filter Payouts{' '}
                <span className="font-normal text-white/80 text-base">
                  – Use the filters below to find specific payout transactions
                </span>
              </h3>
              <div className="mt-2 flex items-center gap-3 sm:mt-0">
                <button type="button" onClick={handleApplyFilters} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 py-2.5 px-5 text-sm font-semibold text-white shadow-lg border-none cursor-pointer">
                  🔍 Search
                </button>
                <button type="button" onClick={handleResetFilters} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 py-2.5 px-5 text-sm font-semibold text-white shadow-lg border-none cursor-pointer">
                  🔄 Reset Filters
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-8 items-start">
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <DarkSelect
                  id="selectedMerchant"
                  value={filters.selectedMerchant}
                  onChange={(v) => handleFilterChange('selectedMerchant', v)}
                  placeholder="All Merchants"
                  options={[
                    { value: '', label: 'All Merchants' },
                    ...merchants.map((m) => ({ value: m.id.toString(), label: `${m.ownerName} (${m.email})` })),
                  ]}
                  className="w-full"
                />
                <input id="fromDate" type="date" value={filters.fromDate} onChange={(e) => handleFilterChange('fromDate', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="toDate" type="date" value={filters.toDate} onChange={(e) => handleFilterChange('toDate', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <DarkSelect
                  id="status"
                  value={filters.status}
                  onChange={(v) => handleFilterChange('status', v)}
                  placeholder="All Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'SUCCESS', label: 'Success' },
                    { value: 'PENDING', label: 'Pending' },
                    { value: 'PROCESSING', label: 'Processing' },
                    { value: 'FAILED', label: 'Failed' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                  ]}
                  className="w-full"
                />
                <input id="amount" type="number" placeholder="Amount" value={filters.amount} onChange={(e) => handleFilterChange('amount', e.target.value)} step="0.01" className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="accountNumber" type="text" placeholder="Account Number" value={filters.accountNumber} onChange={(e) => handleFilterChange('accountNumber', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <input id="transactionId" type="text" placeholder="Transaction ID" value={filters.transactionId} onChange={(e) => handleFilterChange('transactionId', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="merchantReference" type="text" placeholder="Merchant Reference" value={filters.merchantReference} onChange={(e) => handleFilterChange('merchantReference', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="bankReference" type="text" placeholder="Bank Reference" value={filters.bankReference} onChange={(e) => handleFilterChange('bankReference', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
            </div>
          </div>

          {error && (
            <div className="flex flex-col items-center gap-4 py-6 px-6 mb-6 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 font-medium m-0">{error}</p>
              <button type="button" onClick={fetchPayouts} className="py-2 px-5 rounded-lg bg-red-600 text-white font-semibold text-sm cursor-pointer border-none">Try Again</button>
            </div>
          )}

          <div className="flex-1">
            {totalPages > 1 && (
              <div className="flex justify-between items-center py-5 px-5 mb-4 bg-slate-800/80 rounded-xl shadow border border-white/[0.08]">
                <div className="text-sm text-slate-600 font-medium">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} payouts
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="py-2 px-4 border border-white/[0.12] bg-slate-800/80 text-slate-200 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button key={pageNum} type="button" onClick={() => handlePageChange(pageNum)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === pageNum ? 'bg-blue-600 text-white border-blue-600' : 'border border-white/[0.12] bg-slate-800/80 text-slate-200'}`}>{pageNum}</button>
                      );
                    })}
                  </div>
                  <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="py-2 px-4 border border-white/[0.12] bg-slate-800/80 text-slate-200 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                </div>
              </div>
            )}

            {payouts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 bg-white/[0.06] rounded-2xl border border-white/[0.08]">
                <div className="text-5xl mb-4">💳</div>
                <h3 className="text-xl font-bold text-slate-100 mb-2 m-0">No Payouts Found</h3>
                <p className="text-slate-300 mb-4 m-0">No payouts match your current filters.</p>
                <button type="button" onClick={handleResetFilters} className="py-2 px-5 rounded-lg bg-blue-600 text-white font-semibold text-sm cursor-pointer border-none">Clear Filters</button>
              </div>
            ) : (
              <div className="rounded-2xl overflow-x-auto shadow-lg border border-white/[0.08]" style={{ backgroundColor: tableColor }}>
                <table className="w-full min-w-[800px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-gradient-to-br from-slate-50 to-slate-100">
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Date</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Merchant</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Current Merchant Wallet Balance</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Amount</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Account Details</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Transfer Type</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Transaction ID</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Merchant Ref</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Bank Ref</th>
                      <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id} className="hover:bg-white/[0.06]">
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{formatDate(payout.created_at)}</td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                          <div>
                            <div>{payout.user_name || 'N/A'}</div>
                            <div className="text-slate-500 text-xs">{payout.user_email || 'N/A'}</div>
                          </div>
                        </td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">{formatCurrency(payout.merchant_wallet_balance || 0)}</td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle font-medium text-red-600">-{formatCurrency(payout.amount)}</td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                          <div>
                            <div>{payout.account_name}</div>
                            <div className="text-slate-500 text-xs">{payout.account_number}</div>
                            <div className="text-slate-500 text-xs">{payout.bank_name} ({payout.ifsc_code})</div>
                          </div>
                        </td>
                        <td className="py-4 px-4 border-b border-white/[0.08] align-middle">
                          <span className="inline-block py-1 px-2 rounded-lg text-xs font-semibold bg-white/10 text-slate-300">{payout.transfer_type}</span>
                        </td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{payout.viyona_transaction_id || 'N/A'}</td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{payout.merchant_reference_number || 'N/A'}</td>
                        <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{payout.bank_reference_number || 'N/A'}</td>
                        <td className="py-4 px-4 border-b border-white/[0.08] align-middle">
                          <span className={`inline-block py-1 px-3 rounded-full text-xs font-semibold uppercase ${payout.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' : payout.status === 'PENDING' || payout.status === 'PROCESSING' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{payout.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Credit Modal */}
      {showCreditModal && (
        <ManagerCreditModal
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagerCreditHistory;
